import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { SSEEvent } from '@app/shared/types/events'
import type { AuthUser } from '@app/shared/types/entities'

export function handleSSEEvent(
  event: SSEEvent,
  queryClient: QueryClient,
  currentUser: AuthUser | null,
): void {
  const { competitionId } = event

  switch (event.type) {
    case 'connected':
      break

    case 'student.activated':
    case 'student.deactivated':
    case 'question.activated':
    case 'question.skipped':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
      break

    case 'score.drafted':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['participations', event.participationId, 'scores'],
      })
      break

    case 'score.submitted':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['participations', event.participationId, 'scores'],
      })
      break

    case 'score.overridden':
      queryClient.invalidateQueries({
        queryKey: ['scores', event.scoreId],
      })
      if (
        currentUser?.type === 'judge' &&
        event.judgeParticipationId === currentUser.judgeParticipationId
      ) {
        // Don't overwrite the judge's unsent local changes with admin's override.
        // The dirty sync handler will push local changes first, then refetch.
        const cachedScore = queryClient.getQueryData<{ _dirty?: boolean }>([
          'my-score',
          event.participationId,
        ])
        if (!cachedScore?._dirty) {
          queryClient.invalidateQueries({
            queryKey: ['my-score', event.participationId],
          })
        }
      }
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      break

    case 'judge.removed':
      if (
        currentUser?.type === 'judge' &&
        event.judgeParticipationId === currentUser.judgeParticipationId
      ) {
        queryClient.clear()
        window.location.href = '/access-revoked'
      } else {
        queryClient.invalidateQueries({
          queryKey: ['competitions', competitionId, 'judge-participations'],
        })
        queryClient.invalidateQueries({
          queryKey: ['competitions', competitionId, 'live-state'],
        })
      }
      break

    case 'judge-participation.updated':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-participations'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      break

    case 'participation.updated':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'participations'],
      })
      break

    case 'competition.status':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId],
      })
      queryClient.invalidateQueries({ queryKey: ['competitions'] })
      break

    case 'config.updated':
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, event.section],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
      break

    case 'server.shutdown':
      toast.info('جارٍ إعادة الاتصال...')
      break
  }
}
